import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import {
  Alert, Button, ConfirmDialog, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  ErrorState, FilterChip, SegmentedControl, Skeleton, SkeletonRegion, Spinner, phone as phoneLib,
} from '@parsina/framework';
import { useTranslation } from '../../i18n';
import { C } from '../../constants/theme';
import { CT } from '@parsina/framework/tokens';
import { STACK } from '../../constants/density.js';
import { useShortcut, Kbd } from '../../lib/shortcuts.jsx';
import { bffMutation } from '../../lib/bffClient';
import { useClinicDoctors } from '../../hooks/useClinicDoctors';
import { useBookableServices } from '../../hooks/useBookableServices';
import { useBookableSlots, fetchBookableSlots } from '../../hooks/useBookableSlots';
import { usePatientSearch } from '../../hooks/usePatientSearch';
import { useAppointmentNotificationSettings } from '../../hooks/useAppointmentNotifications';
import { bookServiceSession, enqueueServiceTicket } from '../../hooks/useMedicalService';
import { ADD_PATIENT, CREATE_APPOINTMENT, CREATE_APPOINTMENT_NOTIFYING } from '../Booking/constants';
import PhoneInput from '../../components/common/PhoneInput';
import PhoneCandidateList from '../../components/common/PhoneCandidateList';
import { usePhoneCandidates } from '../../hooks/usePhoneCandidates';
import { useClinicRegion } from '../../hooks/useClinicRegion';
import { useCountryDefaults } from '../../hooks/useCountryDefaults';
import { LINK_EXISTING_PATIENT, phoneFlags } from '../../lib/patient';
import { fmtSlotLabel } from '../Booking/components/FirstSlotPicker';
import NotifyPatientField from '../Booking/components/NotifyPatientField';
import InsuranceQuotePanel from '../../components/common/InsuranceQuotePanel';
import { notificationSummary, orderChannels } from '../../lib/appointmentNotifications';
import { clinicTodayIso, addDaysIso, isoDayOf, wallToMs } from '../../lib/clinicZone';
import { faIncludes } from '../Operations/Operations.shared';
import ConnectDoctorModal from './ConnectDoctorModal';
import { usePrepaymentPolicy } from '../../hooks/usePrepaymentPolicy';
import { recordAppointmentDeposit } from '../../hooks/useAppointmentDeposit';
import {
  BookingDepositBlock, DepositAfterBookingFailed, depositFieldError, useBookingDeposit,
} from '../../components/billing/BookingDepositBlock';
import { moneyErrorText } from '../../lib/currencyErrors';
import {
  groupBookableServices, entryFromOrder, defaultEntry, defaultPerformer, resolveDuration, performerRow,
  firstFree, nextFree, slotsOn, gridSlots, mapBookingError, newRequestId,
} from './bookingEngine';

// «ویرایش نوبت» (PM-0102): the row is MOVED, never booked beside. Two documents for
// the reason booking has two — only a move that asked for a notice selects channels.
const RESCHEDULE_APPOINTMENT = `
  mutation RescheduleAppointment($appointmentId: ID!, $newDateTime: String!) {
    rescheduleAppointment(appointmentId: $appointmentId, newDateTime: $newDateTime) { id status dateTime }
  }
`;
const RESCHEDULE_APPOINTMENT_NOTIFYING = `
  mutation RescheduleAppointmentNotifying($appointmentId: ID!, $newDateTime: String!, $notifyChannels: [String!]) {
    rescheduleAppointment(appointmentId: $appointmentId, newDateTime: $newDateTime, notifyChannels: $notifyChannels) {
      id status dateTime
      notifications { channel status reason detail cost }
    }
  }
`;
const SET_APPOINTMENT_NOTE = `
  mutation SetAppointmentNote($appointmentId: ID!, $note: String!) {
    setAppointmentNote(appointmentId: $appointmentId, note: $note) { id reminderNote }
  }
`;

const HORIZON_DAYS = 14;
const LABEL = { fontSize: '0.71875rem', fontWeight: 700, color: C.textSecondary, minWidth: 86, paddingTop: 10 };
const ROW = { display: 'grid', gridTemplateColumns: '86px minmax(0, 1fr)', gap: 12, alignItems: 'start' };

/**
 * «ثبت نوبت» — ONE dialog, service first, keyboard whole (PM-0468 §4.3).
 *
 * Archetype E (a flow inside a `Dialog size="lg" layout="frame"`), opened from
 * «پذیرش» (F4, gated on `appointments.manage`), from «بیماران» (the patient
 * preset — what the retired `Patients/AppointmentModal` did), from
 * «اقدام‌های درمانی» (the order preset: service and patient are facts) and as
 * «ویرایش نوبت» (the time region alone, service and performer locked).
 *
 * The order of questions is the 90 % path in three keystrokes: «ویزیت» is
 * preselected, the ★ performer is preselected with their first free time
 * (UX #11), «اولین وقت آزاد» is the default — so F4 → Enter → the patient →
 * Ctrl+S. Every instant comes from the slot ENGINE (`bookableSlots`, shift
 * anchored, real lengths, the clinic's clock); this file never does `setHours`.
 * The server stays the judge: no optimistic write, and a refusal lands on the
 * field it is about (SERVICE_NOT_IN_SHIFT → the cell, PROVIDER_NOT_PRESENT →
 * the chip, an overlap → the cell plus the engine's next three free times).
 *
 * Props:
 *   preset     — { patient?, serviceCatalogId?, order? }  (see the callers)
 *   reschedule — { appointment: { id, doctorId, doctorName, dateTime, note?,
 *                  serviceCatalogId?, serviceLabel? }, patient: { id, name, phone } }
 *   onNewPatient — the «بیمار جدید» link out (UX #18: no nested overlay); absent = no link
 *   onNeedsShift — (retry) => void: a CASH «پیش‌پرداخت» refused with NO_OPEN_SHIFT
 *                  (PM-0657 §6.2); the caller opens a drawer and runs `retry`
 */
export default function BookingDialog({
  preset = null, reschedule = null, onClose, onBooked, onNewPatient = null, onNeedsShift = null,
}) {
  const { t, formatDate, formatTime, formatDigits, formatMoney, formatNumber } = useTranslation();
  const order = preset?.order || null;
  const editing = reschedule?.appointment || null;

  // ── What can be booked ────────────────────────────────────────────────────
  const services = useBookableServices({ patientFacing: false });
  const { doctors, loading: doctorsLoading, acceptDoctor } = useClinicDoctors();
  const bookableDoctors = useMemo(
    () => (doctors || []).filter((d) => d.status === 'accepted' && d.active !== false), [doctors],
  );
  const pendingDoctors = useMemo(() => (doctors || []).filter((d) => d.status && d.status !== 'accepted'), [doctors]);
  const groups = useMemo(() => groupBookableServices(services.services), [services.services]);
  const allEntries = useMemo(() => [...groups.visits, ...groups.services], [groups]);

  // ── The service ───────────────────────────────────────────────────────────
  const [serviceId, setServiceId] = useState(preset?.serviceCatalogId || null);
  const orderEntry = useMemo(() => entryFromOrder(order), [order]);
  const entry = useMemo(() => {
    if (orderEntry) return orderEntry;
    if (editing) {
      return allEntries.find((e) => e.catalogId === String(editing.serviceCatalogId || '')) || {
        catalogId: editing.serviceCatalogId ? String(editing.serviceCatalogId) : null,
        name: editing.serviceLabel || t('common.entity.visit'), orderType: 'consultation',
        schedulingMode: 'TIMED', rows: [], performers: [],
      };
    }
    return (serviceId && allEntries.find((e) => e.catalogId === serviceId)) || defaultEntry(groups, preset?.serviceCatalogId);
  }, [orderEntry, editing, allEntries, serviceId, groups, preset?.serviceCatalogId, t]);
  const queue = !editing && entry?.schedulingMode === 'QUEUE';
  const serviceLocked = !!order || !!editing;

  // ── The performer ─────────────────────────────────────────────────────────
  const people = useMemo(() => {
    if (editing) {
      return [{
        key: `DOCTOR:${editing.doctorId}`, type: 'DOCTOR', id: editing.doctorId,
        name: editing.doctorName || (doctors || []).find((d) => d.doctorId === editing.doctorId)?.doctorName || '',
        isDefault: true,
      }];
    }
    return entry?.performers || [];
  }, [editing, entry, doctors]);
  const [performerKey, setPerformerKey] = useState(null);
  // A preset performer: «نوبت بعدی» names the dentist; a doctor section's «+ نوبت» names its doctor.
  const presetDoctorId = preset?.patient?.presetDoctorId || preset?.presetDoctorId || null;
  const performer = useMemo(
    () => people.find((p) => p.key === performerKey) || defaultPerformer(people, presetDoctorId),
    [people, performerKey, presetDoctorId],
  );
  const doctorRow = performer?.type === 'DOCTOR' ? (doctors || []).find((d) => d.doctorId === performer.id) || null : null;
  const duration = useMemo(() => {
    const presetMin = Number(preset?.patient?.presetDurationMinutes) || 0;
    if (presetMin > 0) return { minutes: presetMin, source: 'SERVICE' };
    return resolveDuration(entry, performer, doctorRow);
  }, [entry, performer, doctorRow, preset?.patient?.presetDurationMinutes]);
  const minutes = queue ? null : duration.minutes;
  const declarationId = order ? order.declarationId : performerRow(entry, performer)?.declarationId || null;

  // ── The time ──────────────────────────────────────────────────────────────
  const today = clinicTodayIso();
  const startIso = useMemo(() => {
    const floor = Number(preset?.patient?.notBefore) || 0;
    return floor > Date.now() ? isoDayOf(floor) : today;
  }, [preset?.patient?.notBefore, today]);
  const [timeMode, setTimeMode] = useState(editing ? 'pick' : 'first');
  const [dayIso, setDayIso] = useState(editing ? isoDayOf(Number(editing.dateTime)) : startIso);
  const [pickedMs, setPickedMs] = useState(editing ? String(editing.dateTime) : null);
  const [allowOutside, setAllowOutside] = useState(false);
  const [outsideAsk, setOutsideAsk] = useState(null);
  const [horizonFrom, setHorizonFrom] = useState(startIso);
  const [engineKey, setEngineKey] = useState(0);
  const timed = !queue && !!entry;

  const horizon = useBookableSlots({
    enabled: timed, serviceCatalogId: entry?.catalogId || null, performer, fromDate: horizonFrom,
    days: HORIZON_DAYS, durationMinutes: minutes, refreshKey: engineKey,
  });
  const dayRead = useBookableSlots({
    enabled: timed && timeMode === 'pick', serviceCatalogId: entry?.catalogId || null, performer, fromDate: dayIso,
    days: 1, durationMinutes: minutes, refreshKey: engineKey,
  });
  const first = useMemo(() => firstFree(horizon.days, Date.now()), [horizon.days]);
  const effectiveMs = queue ? null : (timeMode === 'first' ? (first ? String(first.startMs) : null) : pickedMs);

  // Each performer's first free time TODAY (UX #11) — one small engine read per chip.
  // (§3.4 names a `servicePerformersWithNextFree` resolver; the BFF shipped the
  // engine alone, so the dialog derives it here, per performer.)
  // A QUEUE service holds no hour, but «does this person have a shift today» is
  // still worth a chip (the day-level warning, §2.3): the probe then asks the
  // engine at a nominal length, and only «no shift» is shown from the answer.
  const perfFirst = usePerformersFirstFree(people, {
    enabled: !!entry && !editing, catalogId: entry?.catalogId || null, today,
    minutesFor: (p) => (Number(preset?.patient?.presetDurationMinutes)
      || resolveDuration(entry, p, p.type === 'DOCTOR' ? (doctors || []).find((d) => d.doctorId === p.id) : null).minutes
      || (queue ? 15 : null)),
  });

  // ── The patient ───────────────────────────────────────────────────────────
  const presetPatient = order
    ? { id: order.patientId, name: order.patientName || '', phone: order.patientPhone || '' }
    : editing ? (reschedule.patient || null) : (preset?.patient?.id ? preset.patient : null);
  const [patient, setPatient] = useState(presetPatient);
  const patientLocked = !!order || !!editing;
  const [query, setQuery] = useState('');
  const search = usePatientSearch(patientLocked || patient ? '' : query);
  const [quick, setQuick] = useState(false);
  const [marker, setMarker] = useState('');
  // «بیمار جدید» (PM-0468 R4 follow-up): a walk-in nobody has registered is
  // registered HERE — mobile + full name, the records already on that mobile shown
  // first — and booked in the same submit. «ثبت سریع» is for a slot held on a phone
  // call; this is for the person standing at the desk. The rest of the file
  // (کد ملی, بیمه, شماره پرونده) is completed later from «تکمیل پرونده».
  const [newMode, setNewMode] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [sharedPhoneOk, setSharedPhoneOk] = useState(false);
  const region = useClinicRegion();
  const countryDefaults = useCountryDefaults();
  const phoneCountry = countryDefaults.phoneCountry || region.country || null;
  const phoneLookup = usePhoneCandidates(newMode ? newPhone : '');
  const phoneHasRecords = newMode && phoneLookup.candidates.length > 0 && !sharedPhoneOk;
  const [requestId, setRequestId] = useState(() => newRequestId());
  const patientRef = useRef(null);
  const serviceRef = useRef(null);

  // ── «گزینه‌های بیشتر» ───────────────────────────────────────────────────────
  // An edit changes two things — the hour and the note — so the note is never
  // folded away there (PM-0102: «a corrected note is saved alone»).
  const [moreOpen, setMoreOpen] = useState(!!editing);
  const [type, setType] = useState('in_person');
  const [companions, setCompanions] = useState([]);
  const [note, setNote] = useState(editing?.note || preset?.patient?.presetNote || '');
  const notifyOn = !order && !!(patient?.id || !quick);
  const { settings: notifyDefaults } = useAppointmentNotificationSettings({ enabled: notifyOn });
  const [notifyPick, setNotifyPick] = useState(null);
  const notifyChannels = notifyPick ?? orderChannels(notifyDefaults?.channels);
  const [insuranceChoice, setInsuranceChoice] = useState(null);

  // ── «پیش‌پرداخت» (PM-0657 §6.2) ───────────────────────────────────────────
  // Only on a fresh booking for a person: never an edit (the نوبت exists; the
  // row's own op takes a deposit), never an order's session, never «ثبت سریع»
  // (no patient to hold it for). Prefilled from the booked service's tariff.
  const { policy: prepaymentPolicy } = usePrepaymentPolicy();
  const deposit = useBookingDeposit({
    policy: prepaymentPolicy,
    priceRial: performerRow(entry, performer)?.unitPrice ?? null,
    applies: !editing && !order && !quick,
  });
  // The booking stood and the deposit did not: `{ id, said }` of the نوبت, the
  // refusal, and whether a retry is in flight.
  const [booked, setBooked] = useState(null);
  const [depositFailure, setDepositFailure] = useState('');
  const [depositRetrying, setDepositRetrying] = useState(false);
  const [requiredAsk, setRequiredAsk] = useState(false);
  const skipRequired = useRef(false);

  // ── Feedback ──────────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({});
  const [nextFreeChips, setNextFreeChips] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const touch = () => setDirty(true);
  const clearError = (field) => setErrors((e) => (e[field] ? { ...e, [field]: null } : e));

  // The performer changes → the picked hour and its override no longer apply.
  useEffect(() => { if (!editing) { setPickedMs(null); setAllowOutside(false); setNextFreeChips([]); } }, [performer?.key, entry?.catalogId, editing]);

  // ── Keyboard: registry only, never a window listener ──────────────────────
  const group = t('reception.actions.book');
  const requestClose = useCallback(() => {
    // After a booking whose deposit failed there is nothing unsaved to lose: the
    // نوبت exists. Leaving is «بستن بدون پیش‌پرداخت».
    if (booked) { onBooked?.(booked.said); onClose?.(); return; }
    if (dirty) setConfirmClose(true); else onClose?.();
  }, [dirty, onClose, booked, onBooked]);
  useShortcut('esc', requestClose, { label: t('common.actions.close'), group, enabled: !confirmClose && !outsideAsk && !requiredAsk });
  useShortcut('f3', () => patientRef.current?.focus(), { label: t('reception.booking.shortcutPatientSearch'), group, enabled: !patientLocked });
  useShortcut('ctrl+s', () => submit(), { label: t('reception.booking.shortcutSubmit'), group, enabled: !submitting });

  // ── Submit ────────────────────────────────────────────────────────────────
  const validate = () => {
    const next = {};
    if (!entry) next.service = t('reception.booking.serviceRequired');
    if (!performer) next.performer = t('reception.booking.performerRequired');
    if (timed && !effectiveMs) next.time = t('reception.booking.timeRequired');
    if (!patientLocked && !patient) {
      if (newMode) {
        if (newName.trim().length < 2) next.patient = t('reception.booking.newPatientNameRequired');
        else if (!phoneLib.isValidPhone(String(newPhone || '').trim())) next.patient = t('reception.booking.newPatientPhoneRequired');
        else if (phoneHasRecords) next.patient = t('reception.booking.newPatientPickRecord');
      } else if (!(quick && marker.trim())) {
        next.patient = t('reception.booking.patientRequired');
      }
    }
    setErrors(next);
    const firstBad = ['service', 'performer', 'time', 'patient'].find((k) => next[k]);
    if (firstBad === 'patient') patientRef.current?.focus();
    if (firstBad === 'service') serviceRef.current?.focus();
    return !firstBad;
  };

  const performerName = performer?.name || '';
  const whenLabel = (ms) => fmtSlotLabel(ms, t, formatDate, formatTime);

  // The deposit alone, for a نوبت already booked — the first try and every retry
  // carry the SAME request id, so the money is taken once (PM-0657 §6.2).
  const runDeposit = async (appointmentId, said) => {
    try {
      await recordAppointmentDeposit({
        appointmentId,
        amountRial: deposit.wire.amountRial,
        paymentMethod: deposit.wire.paymentMethod,
        referenceNumber: deposit.wire.referenceNumber,
        requestId: deposit.wire.requestId,
      });
      setBooked(null);
      setDepositFailure('');
      onBooked?.(`${said} · ${t('reception.deposit.done.taken')}`);
      onClose?.();
      return true;
    } catch (e) {
      setBooked({ id: appointmentId, said });
      setDepositFailure(moneyErrorText(e, t) || t('errors.generic'));
      if (e?.extensions?.code === 'NO_OPEN_SHIFT' && onNeedsShift) {
        onNeedsShift(() => retryDeposit({ id: appointmentId, said }));
      }
      return false;
    }
  };
  const retryDeposit = async (target = booked) => {
    if (!target || depositRetrying) return;
    setDepositRetrying(true);
    try { await runDeposit(target.id, target.said); } finally { setDepositRetrying(false); }
  };

  const submit = async () => {
    if (submitting) return;
    if (booked) { retryDeposit(); return; }
    if (!validate()) return;
    const depositError = depositFieldError(t, deposit);
    if (depositError) { setErrors({ deposit: depositError }); return; }
    // «الزامی» is a question, not a refusal (C9): asked once, then allowed.
    if (!editing && !order && deposit.needsConfirm && !skipRequired.current) {
      setRequiredAsk(true);
      return;
    }
    skipRequired.current = false;
    setSubmitting(true);
    setErrors({});
    setNextFreeChips([]);
    try {
      let said = '';
      if (editing) {
        said = await commitReschedule();
      } else if (order) {
        said = await commitOrder();
      } else {
        said = await commitBooking();
      }
      // Success: a NEW id for the next booking; the same one would have ridden a retry.
      setRequestId(newRequestId());
      // Booked, with a deposit to take: the deposit is its own write. It
      // closes the dialog itself on success and keeps it open on a refusal.
      if (said && typeof said === 'object' && said.depositFor) {
        await runDeposit(said.depositFor, said.said);
        return;
      }
      onBooked?.(said);
      onClose?.();
    } catch (e) {
      const mapped = mapBookingError(e, t);
      setErrors({ [mapped.field]: mapped.message });
      if (mapped.nextFree) setNextFreeChips(nextFree(horizon.days, Number(effectiveMs) || Date.now(), 3));
      // A refused hour stays on screen as the hour it was, marked — the desk
      // sees WHICH cell was refused and re-reads the engine around it.
      if (mapped.field === 'time' && !queue) {
        if (effectiveMs) { setPickedMs(String(effectiveMs)); setDayIso(isoDayOf(Number(effectiveMs))); }
        setTimeMode('pick');
        setEngineKey((k) => k + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  async function commitBooking() {
    let who = patient;
    if (!who?.id && newMode) {
      const created = await bffMutation(ADD_PATIENT, {
        input: { name: newName.trim(), phone: newPhone, ...phoneFlags({ noPhone: false, allowSharedPhone: sharedPhoneOk }) },
      });
      const id = created?.addPatient?.id;
      if (!id) throw new Error(t('errors.reception.bookFailed'));
      // From here the dialog books for THIS record: a booking refused below and
      // retried never registers the same person twice (B07).
      who = { id, name: created.addPatient.name || newName.trim(), phone: created.addPatient.phone || newPhone };
      setPatient(who);
      setNewMode(false);
    }
    const notify = who?.id && notifyChannels?.length ? notifyChannels : null;
    const input = {
      ...(who?.id ? { patientId: who.id } : { placeholderLabel: marker.trim() }),
      performer: { type: performer.type, id: performer.id },
      ...(performer.type === 'DOCTOR' ? { doctorId: performer.id } : {}),
      serviceCatalogId: entry.catalogId,
      serviceDeclarationId: declarationId || undefined,
      ...(queue
        // A QUEUE service holds no hour: a same-day turn, ordered by the desk (B06).
        ? { untimed: 'DAY', dateTime: String(wallToMs(today, 12 * 60)) }
        : { dateTime: String(effectiveMs), ...(allowOutside && pickedMs === effectiveMs ? { allowOutsideServiceWindow: true } : {}) }),
      type: type || 'in_person',
      reminderNote: note.trim() || undefined,
      services: companions.length ? companions : undefined,
      insuranceChoice: insuranceChoice || undefined,
      notifyChannels: notify || undefined,
      followUpId: preset?.patient?.followUpId || undefined,
    };
    const res = await bffMutation(notify ? CREATE_APPOINTMENT_NOTIFYING : CREATE_APPOINTMENT, { input });
    const booked = res?.createAppointment;
    if (!booked?.id) throw new Error(t('errors.reception.bookFailed'));
    const when = booked.dateTime && !queue ? formatTime(Number(booked.dateTime)) : t('common.dates.today');
    const msg = t('reception.booking.bookedToast', { when, performer: performerName });
    const notified = notificationSummary(booked.notifications, t);
    const said = notified ? `${msg} · ${notified}` : msg;
    return deposit.wire ? { depositFor: booked.id, said } : said;
  }

  async function commitOrder() {
    const who = order.patientName || t('common.entity.patient');
    if (queue) {
      const q = await enqueueServiceTicket({
        orderId: order.id, patientId: order.patientId, declarationId: order.declarationId,
        performer: { type: performer.type, id: performer.id },
      });
      const mine = (q?.entries || []).find((e) => e.patientId === order.patientId);
      return t('reception.paraclinic.queueTicket', { ticket: formatDigits(mine?.ticketNo ?? '—'), performer: performerName });
    }
    const booked = await bookServiceSession({
      orderId: order.id, patientId: order.patientId, declarationId: order.declarationId,
      performer: { type: performer.type, id: performer.id }, dateTimeMs: Number(effectiveMs), reminderNote: note.trim() || undefined,
      sessionId: order.sessionId || undefined,
    });
    return t('reception.paraclinic.bookedAt', { who, performer: performerName, when: whenLabel(booked?.dateTime || effectiveMs) });
  }

  async function commitReschedule() {
    const who = reschedule.patient?.name || t('common.entity.patient');
    const moving = effectiveMs && String(effectiveMs) !== String(editing.dateTime);
    const trimmed = note.trim();
    const noteChanged = trimmed !== String(editing.note || '').trim();
    const said = [];
    if (moving) {
      const notify = reschedule.patient?.id && notifyChannels?.length ? notifyChannels : null;
      const res = await bffMutation(notify ? RESCHEDULE_APPOINTMENT_NOTIFYING : RESCHEDULE_APPOINTMENT, {
        appointmentId: editing.id, newDateTime: String(effectiveMs), ...(notify ? { notifyChannels: notify } : {}),
      });
      const moved = res?.rescheduleAppointment;
      if (!moved?.id) throw new Error(t('reception.edit.failed'));
      said.push(t('reception.edit.movedTo', { who, when: whenLabel(moved.dateTime || effectiveMs) }));
      const notified = notificationSummary(moved.notifications, t);
      if (notified) said.push(notified);
    }
    if (noteChanged) {
      try {
        await bffMutation(SET_APPOINTMENT_NOTE, { appointmentId: editing.id, note: trimmed });
        if (!moving) said.push(t('reception.edit.noteSaved', { who }));
      } catch (e) {
        if (!moving) throw e;
        said.push(t('reception.edit.noteFailed'));
      }
    }
    return said.length ? said.join(' · ') : null;
  }

  // ── Nothing to book for → connect a practitioner, not an empty form ───────
  if (!order && !editing && services.loaded && allEntries.length === 0 && !doctorsLoading && bookableDoctors.length === 0) {
    return <ConnectDoctorModal pending={pendingDoctors} onAccept={acceptDoctor} onClose={onClose} />;
  }

  const pickSlot = (cell) => {
    touch();
    clearError('time');
    setNextFreeChips([]);
    if (cell.held) { setOutsideAsk(cell); return; }
    setAllowOutside(false);
    setPickedMs(cell.value);
    setTimeMode('pick');
  };

  const companionCandidates = groups.services.filter((e) => e.catalogId !== entry?.catalogId && (e.rows[0]?.unitPrice > 0));
  const quoteLine = order || editing ? null
    : !patient?.id
      ? (quick ? t('reception.booking.quoteQuick') : t('reception.booking.quoteNoPatient'))
      : null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) requestClose(); }}>
      <DialogContent
        size="lg"
        layout="frame"
        data-testid="reception-booking-dialog"
        data-request-id={requestId}
        data-booked-id={booked?.id || undefined}
        onOpenAutoFocus={(e) => { e.preventDefault(); (serviceLocked || patientLocked ? null : serviceRef.current)?.focus?.(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{editing ? t('reception.edit.title') : (order?.bookingTitle || t('reception.actions.book'))}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {booked ? (
            <DepositAfterBookingFailed
              reason={depositFailure}
              retrying={depositRetrying}
              onRetry={() => retryDeposit()}
              onCloseWithout={requestClose}
            />
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
            {errors.form ? <Alert variant="error" data-testid="reception-booking-form-error">{errors.form}</Alert> : null}

            {/* ── 1. خدمت ─────────────────────────────────────────────────── */}
            <div style={ROW}>
              <span style={LABEL}>{t('reception.booking.service')}</span>
              {serviceLocked ? (
                <div data-testid="reception-booking-locked-service" style={{ paddingTop: 9, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                  {t('reception.booking.lockedService', { service: entry?.name || '' })}
                </div>
              ) : (
                <ServiceCombobox
                  ref={serviceRef}
                  groups={groups}
                  value={entry?.catalogId || null}
                  loading={services.loading && !services.loaded}
                  error={services.error}
                  onRetry={services.reload}
                  onChange={(id) => { setServiceId(id); setPerformerKey(null); touch(); clearError('service'); }}
                  onCommit={() => patientRef.current?.focus()}
                  fieldError={errors.service}
                />
              )}
            </div>

            {/* ── 2. انجام‌دهنده ─────────────────────────────────────────── */}
            <div style={ROW}>
              <span style={LABEL}>{t('reception.booking.performer')}</span>
              {editing ? (
                <div data-testid="reception-booking-locked-performer" style={{ paddingTop: 9, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                  {t('reception.booking.lockedPerformer', { performer: people[0]?.name || '' })}
                </div>
              ) : (
                <PerformerChips
                  people={people}
                  selected={performer}
                  firstFree={perfFirst}
                  queue={queue}
                  error={errors.performer}
                  onPick={(p) => { setPerformerKey(p.key); touch(); clearError('performer'); }}
                  t={t}
                  formatTime={formatTime}
                />
              )}
            </div>

            {/* ── 3. زمان ───────────────────────────────────────────────── */}
            <div style={ROW}>
              <span style={LABEL}>{t('reception.booking.time')}</span>
              <div
                data-testid="reception-booking-time"
                data-mode={queue ? 'ticket' : timeMode}
                data-ms={effectiveMs || undefined}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {queue ? (
                  <Alert variant="info">{t('reception.booking.queueMode')}</Alert>
                ) : (
                  <>
                    {editing ? (
                      // Where the نوبت is NOW, stated before any hour is touched — the
                      // grid below is where it can move to (PM-0102).
                      <div data-testid="reception-booking-current" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                          {t('reception.edit.currentTime', { when: whenLabel(Number(editing.dateTime)) })}
                        </span>
                        <span style={{ fontSize: '0.71875rem', color: C.textMuted }}>{t('reception.edit.hint')}</span>
                      </div>
                    ) : null}
                    {!editing && (
                      <SegmentedControl
                        size="sm"
                        semantics="filter"
                        ariaLabel={t('reception.booking.time')}
                        value={timeMode}
                        onChange={(v) => { setTimeMode(v); touch(); clearError('time'); }}
                        options={[
                          { value: 'first', label: t('reception.booking.modeFirstFree') },
                          { value: 'pick', label: t('reception.booking.modePick') },
                        ]}
                      />
                    )}
                    <div data-testid="reception-booking-length" style={{ fontSize: '0.71875rem', color: C.textMuted }}>
                      {minutes
                        ? (duration.source === 'DOCTOR'
                          ? t('reception.booking.lengthFromDoctor', { minutes: formatDigits(minutes) })
                          : t('reception.booking.lengthFromService', { minutes: formatDigits(minutes) }))
                        : null}
                    </div>

                    {timeMode === 'first' ? (
                      <FirstFreeLine
                        read={horizon}
                        first={first}
                        onLater={() => setHorizonFrom((d) => addDaysIso(d, HORIZON_DAYS))}
                        whenLabel={whenLabel}
                        t={t}
                        formatDigits={formatDigits}
                      />
                    ) : (
                      <DayPicker
                        dayIso={dayIso}
                        today={today}
                        onDay={(iso) => { setDayIso(iso); touch(); }}
                        read={dayRead}
                        cells={gridSlots(slotsOn(dayRead.days, dayIso), { formatTime, t, nowMs: Date.now() })}
                        pickedMs={pickedMs}
                        onPick={pickSlot}
                        t={t}
                        formatDate={formatDate}
                      />
                    )}

                    {errors.time ? (
                      <div role="alert" data-testid="reception-booking-time-error" style={{ fontSize: '0.75rem', color: C.red }}>{errors.time}</div>
                    ) : null}
                    {nextFreeChips.length > 0 ? (
                      <div data-testid="reception-booking-next-free" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.71875rem', color: C.textMuted }}>{t('reception.booking.nextFree')}</span>
                        {nextFreeChips.map((s) => (
                          <FilterChip
                            key={s.startMs}
                            label={whenLabel(s.startMs)}
                            data-ms={String(s.startMs)}
                            onClick={() => { setDayIso(s.date); pickSlot({ value: String(s.startMs), held: false }); }}
                          />
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {/* ── «پیش‌پرداخت» (PM-0657 §6.2) — under the service and the time ── */}
            {deposit.shown ? (
              <div style={ROW}>
                <span style={LABEL} />
                <BookingDepositBlock deposit={deposit} error={errors.deposit} onTouch={() => { touch(); clearError('deposit'); }} />
              </div>
            ) : null}

            {/* ── 4. بیمار ──────────────────────────────────────────────── */}
            <div style={ROW}>
              <span style={LABEL}>{t('reception.booking.patient')}</span>
              <PatientField
                patient={patient}
                locked={patientLocked}
                query={query}
                onQuery={(v) => { setQuery(v); touch(); clearError('patient'); }}
                search={search}
                onPick={(p) => { setPatient(p); setQuery(''); setQuick(false); setNewMode(false); touch(); clearError('patient'); }}
                onClear={() => { setPatient(null); touch(); setTimeout(() => patientRef.current?.focus(), 0); }}
                quick={quick}
                onQuick={() => { setQuick((q) => !q); setNewMode(false); touch(); clearError('patient'); }}
                newMode={newMode}
                onNewMode={(fromQuery) => {
                  setNewMode((on) => !on || fromQuery != null);
                  setQuick(false);
                  // What was typed into the search seeds the form: digits are the
                  // mobile, anything else the name.
                  if (fromQuery != null) {
                    const q = String(fromQuery).trim();
                    if (/^[+0-9۰-۹٠-٩\s-]+$/.test(q)) setNewPhone(q); else setNewName(q);
                  }
                  setQuery('');
                  touch(); clearError('patient');
                }}
                newFields={newMode ? (
                  <NewPatientFields
                    name={newName}
                    onName={(v) => { setNewName(v); touch(); clearError('patient'); }}
                    phone={newPhone}
                    onPhone={(v) => { setNewPhone(v); setSharedPhoneOk(false); touch(); clearError('patient'); }}
                    phoneCountry={phoneCountry}
                    candidates={phoneLookup.candidates}
                    sharedPhoneOk={sharedPhoneOk}
                    onChooseLinked={(c) => {
                      setPatient(c.patient || { id: c.patientId, name: c.displayName, phone: phoneLookup.number });
                      setNewMode(false); clearError('patient');
                    }}
                    onLink={async (c) => {
                      const data = await bffMutation(LINK_EXISTING_PATIENT, { patientId: c.patientId });
                      const linked = data?.linkExistingPatient;
                      if (!linked?.id) throw new Error('linkExistingPatient returned no patient');
                      setPatient(linked); setNewMode(false); clearError('patient');
                    }}
                    onSameNumber={() => { setSharedPhoneOk(true); clearError('patient'); }}
                    t={t}
                  />
                ) : null}
                marker={marker}
                onMarker={(v) => { setMarker(v); touch(); clearError('patient'); }}
                inputRef={patientRef}
                error={errors.patient}
                onNewPatient={onNewPatient}
                t={t}
                formatDigits={formatDigits}
              />
            </div>

            {/* ── 5. گزینه‌های بیشتر ───────────────────────────────────── */}
            {(
              <div>
                <button
                  type="button"
                  onClick={() => setMoreOpen((o) => !o)}
                  aria-expanded={moreOpen}
                  data-testid="reception-booking-more"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none', padding: 0,
                    fontFamily: 'inherit', fontSize: '0.78125rem', fontWeight: 700, color: 'var(--p-navy)', cursor: 'pointer',
                  }}
                >
                  <ChevronDown size={14} style={{ transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} aria-hidden />
                  {t('reception.booking.moreOptions')}
                </button>
                {moreOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular, marginTop: 10 }}>
                    {!editing && !order && (
                      <div style={ROW}>
                        <span style={LABEL}>{t('reception.booking.visitType')}</span>
                        <SegmentedControl
                          size="sm"
                          semantics="filter"
                          ariaLabel={t('reception.booking.visitType')}
                          value={type}
                          onChange={(v) => { setType(v); touch(); }}
                          options={[
                            { value: 'in_person', label: t('reception.booking.inPerson') },
                            { value: 'online', label: t('reception.booking.online') },
                          ]}
                        />
                      </div>
                    )}
                    {!editing && !order && companionCandidates.length > 0 && (
                      <div style={ROW}>
                        <span style={LABEL}>{t('reception.booking.companions')}</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="reception-booking-companions">
                          {companionCandidates.map((e) => {
                            const on = companions.some((c) => c.catalogId === e.catalogId);
                            const price = e.rows[0]?.unitPrice;
                            return (
                              <FilterChip
                                key={e.catalogId}
                                label={price ? `${e.name} · ${formatMoney(price, { currency: e.rows[0]?.currency })}` : e.name}
                                active={on}
                                onClick={() => {
                                  touch();
                                  setCompanions((prev) => (on ? prev.filter((c) => c.catalogId !== e.catalogId) : [...prev, { catalogId: e.catalogId, quantity: 1 }]));
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div style={ROW}>
                      <label htmlFor="reception-booking-note" style={LABEL}>{t('reception.booking.note')}</label>
                      <textarea
                        id="reception-booking-note"
                        data-testid="reception-booking-note"
                        value={note}
                        onChange={(e) => { setNote(e.target.value); touch(); }}
                        placeholder={t('reception.booking.notePlaceholder')}
                        rows={2}
                        style={{
                          width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.8125rem',
                          padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`,
                        }}
                      />
                    </div>
                    {!order && (patient?.id || editing) && (
                      <div style={ROW}>
                        <span style={LABEL} />
                        <NotifyPatientField value={notifyChannels} onChange={(v) => { setNotifyPick(v); touch(); }} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* The quote — always answered: the figure, or why there is none yet (B05). */}
            {!order && !editing && patient?.id && !queue ? (
              <InsuranceQuotePanel
                patientId={patient.id}
                doctorId={performer?.type === 'DOCTOR' ? performer.id : null}
                services={companions}
                serviceDeclarationId={declarationId}
                choice={insuranceChoice}
                onChoiceChange={(c) => { setInsuranceChoice(c); touch(); }}
              />
            ) : null}
          </div>
          )}
        </DialogBody>

        {booked ? null : (
        <DialogFooter>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
            <span data-testid="reception-booking-quote" style={{ fontSize: '0.75rem', color: C.textMuted, flex: 1, minWidth: 0 }}>
              {quoteLine}
            </span>
            <Button variant="ghost" onClick={requestClose} disabled={submitting}>{t('common.actions.cancel')}</Button>
            <Button onClick={submit} disabled={submitting} data-testid="reception-booking-submit">
              {submitting ? <Spinner size="sm" /> : null}
              {editing ? t('reception.edit.save') : (queue ? t('reception.booking.ticketSubmit') : t('reception.actions.book'))}
              <Kbd combo="ctrl+s" />
            </Button>
          </div>
        </DialogFooter>
        )}

        {/* «الزامی» (C9): booking without a deposit is asked about, never refused. */}
        <ConfirmDialog
          open={requiredAsk}
          onOpenChange={(o) => { if (!o) setRequiredAsk(false); }}
          title={t('reception.booking.deposit.requiredTitle')}
          description={t('reception.booking.deposit.requiredConfirm')}
          confirmLabel={t('reception.booking.deposit.requiredConfirmAction')}
          cancelLabel={t('reception.booking.deposit.back')}
          data-testid="booking-deposit-required-confirm"
          onConfirm={() => {
            setRequiredAsk(false);
            skipRequired.current = true;
            submit();
          }}
        />

        {/* MARK (decision Q3): the hour the shift admits but the service does not — ask, then allow. */}
        <ConfirmDialog
          open={!!outsideAsk}
          onOpenChange={(o) => { if (!o) setOutsideAsk(null); }}
          title={t('reception.booking.outsideWindow')}
          description={t('reception.booking.outsideWindowConfirm', { service: entry?.name || '' })}
          confirmLabel={t('reception.booking.outsideWindowYes')}
          cancelLabel={t('common.actions.cancel')}
          data-testid="reception-booking-outside-window"
          onConfirm={() => {
            setAllowOutside(true);
            setPickedMs(outsideAsk.value);
            setTimeMode('pick');
            setOutsideAsk(null);
          }}
        />

        <ConfirmDialog
          open={confirmClose}
          onOpenChange={(o) => { if (!o) setConfirmClose(false); }}
          title={t('reception.booking.dirtyTitle')}
          description={t('common.actions.unsavedChanges')}
          confirmLabel={t('reception.booking.dirtyLeave')}
          cancelLabel={t('common.actions.cancel')}
          destructive
          onConfirm={() => { setConfirmClose(false); onClose?.(); }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pieces
// ─────────────────────────────────────────────────────────────────────────────

/** One engine read per performer for TODAY: their first free time, or «no shift». */
export function usePerformersFirstFree(people, { enabled, catalogId, today, minutesFor }) {
  const [state, setState] = useState({});
  const keys = people.map((p) => p.key).join(',');
  useEffect(() => {
    // An empty answer stays the SAME object: a fresh {} would re-render the caller for nothing.
    if (!enabled || !people.length) { setState((s) => (Object.keys(s).length ? {} : s)); return undefined; }
    let live = true;
    setState(Object.fromEntries(people.map((p) => [p.key, { loading: true }])));
    people.forEach(async (p) => {
      const minutes = minutesFor(p);
      if (!(minutes > 0)) { if (live) setState((s) => ({ ...s, [p.key]: { loading: false, firstMs: null, noShift: true } })); return; }
      try {
        const days = await fetchBookableSlots({ serviceCatalogId: catalogId, performer: p, fromDate: today, days: 1, durationMinutes: minutes });
        const hit = firstFree(days, Date.now());
        const noShift = !days.length || days.every((d) => !(d.slots || []).length);
        if (live) setState((s) => ({ ...s, [p.key]: { loading: false, firstMs: hit?.startMs || null, noShift } }));
      } catch {
        if (live) setState((s) => ({ ...s, [p.key]: { loading: false, firstMs: null, failed: true } }));
      }
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, catalogId, today, keys]);
  return state;
}

function PerformerChips({ people, selected, firstFree: perfFirst, queue, error, onPick, t, formatTime }) {
  const single = people.length === 1;
  const onKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) || !people.length) return;
    e.preventDefault();
    const at = Math.max(0, people.findIndex((p) => p.key === selected?.key));
    const rtl = (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');
    const forward = (e.key === 'ArrowRight') !== rtl;
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? people.length - 1
      : (at + (forward ? 1 : people.length - 1)) % people.length;
    onPick(people[next]);
    e.currentTarget.querySelectorAll('button')[next]?.focus();
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        role="group"
        aria-label={t('reception.booking.performer')}
        data-testid="reception-booking-performers"
        data-state={single ? 'single' : people.length ? 'many' : 'none'}
        onKeyDown={onKeyDown}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}
      >
        {people.map((p) => {
          const on = selected?.key === p.key;
          const ff = perfFirst[p.key];
          const meta = !ff || ff.loading ? (queue ? null : '…')
            : (ff.noShift && p.type === 'STAFF') ? t('reception.booking.noHours')
              : queue ? null
                : ff.firstMs ? t('reception.booking.firstFreeAt', { when: formatTime(ff.firstMs) })
                  : t('reception.booking.noFreeToday');
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={on}
              tabIndex={on ? 0 : -1}
              data-testid={`reception-booking-performer-${p.id}`}
              data-first-ms={ff?.firstMs ? String(ff.firstMs) : undefined}
              onClick={() => onPick(p)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
                border: `1px solid ${on ? 'var(--p-navy)' : error ? C.red : C.border}`,
                background: on ? 'var(--p-navy)' : '#fff', color: on ? '#fff' : C.textPrimary,
                fontFamily: 'inherit', fontSize: '0.78125rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {p.isDefault ? <span aria-hidden>★</span> : null}
              {p.name}
              {meta ? <span style={{ opacity: on ? 0.85 : 0.7, fontSize: '0.6875rem' }}>· {meta}</span> : null}
            </button>
          );
        })}
        {!people.length ? (
          <span style={{ fontSize: '0.75rem', color: CT.warnInk }} data-testid="reception-booking-no-performer">{t('reception.booking.noPerformer')}</span>
        ) : null}
      </div>
      {error ? <div role="alert" data-testid="reception-booking-performer-error" style={{ fontSize: '0.75rem', color: C.red }}>{error}</div> : null}
    </div>
  );
}

function FirstFreeLine({ read, first, onLater, whenLabel, t, formatDigits }) {
  if (!read.ready) return <div style={{ fontSize: '0.75rem', color: C.textMuted }}>{t('reception.booking.performerRequired')}</div>;
  if (read.loading && !read.days.length) {
    return <SkeletonRegion label={t('common.state.loading')} style={{ maxWidth: 320 }}><Skeleton height={40} /></SkeletonRegion>;
  }
  if (read.error) {
    return <ErrorState title={t('reception.booking.slotsFailed')} description={read.error} retryLabel={t('common.actions.retry')} onRetry={read.reload} data-testid="reception-booking-time-errorstate" />;
  }
  if (!first) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-testid="reception-booking-first-free" data-state="empty">
        <span style={{ fontSize: '0.78125rem', color: CT.warnInk }}>{t('reception.booking.noFreeInHorizon', { days: formatDigits(HORIZON_DAYS) })}</span>
        <Button variant="outline" size="sm" onClick={onLater}>{t('reception.booking.showLaterDays')}</Button>
      </div>
    );
  }
  return (
    <div
      data-testid="reception-booking-first-free"
      data-ms={String(first.startMs)}
      data-state="content"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10,
        border: `1.5px solid ${CT.accent}`, background: 'color-mix(in srgb, var(--p-teal) 10%, transparent)',
        fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, alignSelf: 'flex-start',
      }}
    >
      <CalendarDays size={15} aria-hidden />
      {whenLabel(first.startMs)}
    </div>
  );
}

/**
 * The day stepper and the grid. App-local cells rather than the kit `SlotGrid`:
 * the kit draws free/taken/held with no per-cell REASON (title, tone), and the
 * desk's law is that a taken hour says why (DC §12.3) — the kit gap is noted in
 * CL §5; the geometry and testid shape follow the kit so a promotion is a swap.
 */
function DayPicker({ dayIso, today, onDay, read, cells, pickedMs, onPick, t, formatDate }) {
  const dayDate = new Date(wallToMs(dayIso, 12 * 60));
  const state = !read.ready ? 'empty' : read.loading && !read.days.length ? 'loading' : read.error ? 'error' : cells.length ? 'content' : 'empty';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Button variant="outline" size="sm" onClick={() => onDay(addDaysIso(dayIso, -1))} aria-label={t('reception.booking.dayPrev')} data-testid="reception-booking-day-prev">
          <ChevronRight size={14} aria-hidden />
        </Button>
        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, minWidth: 160, textAlign: 'center' }} data-testid="reception-booking-day" data-iso={dayIso}>
          {dayIso === today ? `${t('common.dates.today')} · ` : ''}{formatDate(dayDate, 'dayMonth')}
        </span>
        <Button variant="outline" size="sm" onClick={() => onDay(addDaysIso(dayIso, 1))} aria-label={t('reception.booking.dayNext')} data-testid="reception-booking-day-next">
          <ChevronLeft size={14} aria-hidden />
        </Button>
        {dayIso !== today ? (
          <Button variant="ghost" size="sm" onClick={() => onDay(today)} data-testid="reception-booking-day-today">{t('common.dates.today')}</Button>
        ) : null}
      </div>
      <div
        role="group"
        aria-label={t('reception.booking.time')}
        data-testid="reception-booking-grid"
        data-state={state}
        style={state === 'content' || state === 'loading'
          ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(5rem, 1fr))', gap: '0.5rem' }
          : undefined}
      >
        {state === 'loading' ? Array.from({ length: 12 }, (_, i) => <Skeleton key={i} height={44} />) : null}
        {state === 'error' ? (
          <ErrorState title={t('reception.booking.slotsFailed')} description={read.error} retryLabel={t('common.actions.retry')} onRetry={read.reload} />
        ) : null}
        {state === 'empty' ? (
          <div style={{ fontSize: '0.78125rem', color: C.textMuted, padding: '10px 0' }}>{t('reception.booking.noShiftThisDay')}</div>
        ) : null}
        {state === 'content' ? cells.map((cell) => {
          const selected = pickedMs != null && cell.value === pickedMs;
          return (
            <button
              key={cell.value}
              type="button"
              onClick={cell.disabled ? undefined : () => onPick(cell)}
              disabled={cell.disabled}
              aria-pressed={selected}
              title={cell.title}
              data-testid={`reception-booking-slot-${cell.value}`}
              data-state={cell.disabled ? 'disabled' : selected ? 'selected' : 'default'}
              data-held={cell.held || undefined}
              data-reason={cell.reason || undefined}
              className="tabular-nums"
              style={{
                minHeight: '2.75rem', padding: '0.5rem 0.625rem', borderRadius: 10, fontSize: '0.8125rem', fontWeight: 600,
                fontFamily: 'inherit', cursor: cell.disabled ? 'not-allowed' : 'pointer',
                border: selected ? `2px solid ${CT.accent}` : cell.held ? `1.5px dashed ${CT.warn}` : `1.5px solid ${C.border}`,
                background: selected ? 'color-mix(in srgb, var(--p-teal) 14%, transparent)' : cell.held ? 'rgba(217,119,6,0.10)' : C.card,
                color: selected ? CT.accentInk : cell.held ? CT.warnInk : C.textPrimary,
                opacity: cell.disabled ? 0.45 : 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              }}
            >
              <span>{cell.label}</span>
              {cell.held ? <span style={{ fontSize: '0.625rem', fontWeight: 600 }}>{t('reception.booking.outsideWindow')}</span> : null}
            </button>
          );
        }) : null}
      </div>
    </div>
  );
}

function PatientField({
  patient, locked, query, onQuery, search, onPick, onClear, quick, onQuick, marker, onMarker, inputRef, error,
  onNewPatient, newMode, onNewMode, newFields, t, formatDigits,
}) {
  const [open, setOpen] = useState(false);
  const hits = search.results || [];
  if (patient) {
    return (
      <div data-testid="reception-booking-patient" data-patient={patient.id || ''} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.84375rem', fontWeight: 700, color: C.textPrimary }}>{patient.name || t('patients.placeholder.noName')}</span>
        {patient.phone ? <span style={{ fontSize: '0.75rem', color: C.textMuted }} dir="ltr">{formatDigits(phoneLib.forDisplay(patient.phone))}</span> : null}
        {!locked ? <Button variant="ghost" size="sm" onClick={onClear}>{t('common.actions.change')}</Button> : null}
      </div>
    );
  }
  return (
    <div data-testid="reception-booking-patient" data-patient="" style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} aria-hidden style={{ position: 'absolute', insetInlineStart: 10, top: 13, color: C.textMuted }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { onQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) { e.preventDefault(); onPick(hits[0]); } }}
            placeholder={t('reception.booking.patientSearchPlaceholder')}
            aria-label={t('reception.booking.patient')}
            aria-invalid={!!error}
            data-testid="reception-booking-patient-input"
            style={{
              width: '100%', boxSizing: 'border-box', height: 40, paddingInlineStart: 30, paddingInlineEnd: 10,
              borderRadius: 8, border: `1px solid ${error ? C.red : C.border}`, fontFamily: 'inherit', fontSize: '0.8125rem',
            }}
          />
          {open && search.active ? (
            <div
              role="listbox"
              data-testid="reception-booking-patient-results"
              style={{
                position: 'absolute', insetInline: 0, top: 44, zIndex: 5, background: '#fff', borderRadius: 10,
                border: `1px solid ${C.border}`, boxShadow: '0 12px 30px rgba(15,28,46,.14)', maxHeight: 240, overflowY: 'auto',
              }}
            >
              {search.loading && !hits.length ? (
                <div style={{ padding: 10, fontSize: '0.75rem', color: C.textMuted }}><Spinner size="sm" /></div>
              ) : !hits.length ? (
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: '0.75rem', color: C.textMuted }}>{t('reception.booking.patientNoneFound')}</span>
                  {/* Nobody by that name or number: register them, seeded with what was typed. */}
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onNewMode(query)}
                    data-testid="reception-booking-new-from-query"
                    style={{ alignSelf: 'flex-start', border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', fontSize: '0.78125rem', fontWeight: 700, color: 'var(--p-navy)', cursor: 'pointer' }}
                  >
                    {t('reception.booking.newPatientFromQuery', { query: query.trim() })}
                  </button>
                </div>
              ) : hits.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(p)}
                  data-testid={`reception-booking-patient-hit-${p.id}`}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '8px 10px', border: 'none',
                    borderBottom: `1px solid ${C.border}`, background: 'transparent', fontFamily: 'inherit', textAlign: 'start', cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>{p.name}</span>
                  <span style={{ fontSize: '0.71875rem', color: C.textMuted }} dir="ltr">{p.phone ? formatDigits(phoneLib.forDisplay(p.phone)) : ''}</span>
                  {p.fileNumber ? <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('reception.day.fileNumberChip', { number: formatDigits(p.fileNumber) })}</span> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <Kbd combo="f3" />
        <FilterChip label={t('reception.booking.newPatient')} active={newMode} onClick={() => onNewMode(null)} data-testid="reception-booking-new" />
        <FilterChip label={t('reception.booking.quickRegister')} active={quick} onClick={onQuick} data-testid="reception-booking-quick" />
      </div>
      {newFields}
      {quick ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            value={marker}
            onChange={(e) => onMarker(e.target.value)}
            placeholder={t('reception.booking.markerPlaceholder')}
            aria-label={t('reception.booking.markerLabel')}
            data-testid="reception-booking-marker"
            style={{ height: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', fontSize: '0.8125rem' }}
          />
          <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('reception.booking.quickOn')}</span>
        </div>
      ) : null}
      {onNewPatient ? (
        <button type="button" onClick={onNewPatient} data-testid="reception-booking-new-patient" style={{ alignSelf: 'flex-start', border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', fontSize: '0.71875rem', fontWeight: 700, color: 'var(--p-navy)', textDecoration: 'underline', cursor: 'pointer' }}>
          {t('reception.booking.newPatientLink')}
        </button>
      ) : null}
      {error ? <div role="alert" data-testid="reception-booking-patient-error" style={{ fontSize: '0.75rem', color: C.red }}>{error}</div> : null}
    </div>
  );
}

/**
 * «بیمار جدید» inside «ثبت نوبت»: the mobile first (where focus lands — the desk
 * asks for the number before anything else), the records already on it right
 * under it, then the full name. A household shares a mobile, so a number with
 * records waits for the desk's pick: one of them, or «بیمار جدید با همین شماره».
 */
function NewPatientFields({
  name, onName, phone, onPhone, phoneCountry, candidates, sharedPhoneOk, onChooseLinked, onLink, onSameNumber, t,
}) {
  return (
    <div data-testid="reception-booking-new-fields" style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight, padding: '8px 10px', borderRadius: 8, background: C.bg }}>
      <PhoneInput
        key={phoneCountry || ''}
        autoFocus
        defaultCountry={phoneCountry || undefined}
        label={t('common.fields.mobile')}
        required
        value={phone}
        onChange={(e164) => onPhone(e164)}
        data-testid="reception-booking-new-phone"
      />
      {candidates.length > 0 && !sharedPhoneOk ? (
        <>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textSecondary }}>{t('reception.booking.phoneCandidatesHint')}</div>
          <PhoneCandidateList
            candidates={candidates}
            onChooseLinked={onChooseLinked}
            chooseLabel={t('patients.phoneCandidates.choose')}
            onLink={onLink}
            linkLabel={t('patients.phoneCandidates.addAndChoose')}
            onNewPatient={onSameNumber}
            data-testid="reception-booking-new-candidates"
          />
        </>
      ) : null}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 600, color: C.textSecondary }}>
        {t('common.fields.fullName')}
        <input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder={t('patients.form.fullNamePlaceholder')}
          data-testid="reception-booking-new-name"
          style={{ height: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', fontSize: '0.8125rem' }}
        />
      </label>
      <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('reception.booking.newPatientHint')}</span>
    </div>
  );
}

/**
 * «خدمت» — a combobox over the bookable services, grouped «ویزیت» / «خدمات»;
 * typing filters, arrows move, Enter chooses (or, with a choice already made,
 * commits the step and moves on to the patient). App-local: the kit exports no
 * Combobox yet (CL §5 gap); `ServiceCombobox` in «خدمات» is the same shape over
 * the dictionary rather than the bookable list.
 */
function ServiceComboboxInner({
  groups, value, loading, error, onRetry, onChange, onCommit, fieldError,
}, ref) {
  const { t, formatDigits } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const all = [...groups.visits.map((e) => ({ ...e, group: 'visit' })), ...groups.services.map((e) => ({ ...e, group: 'service' }))];
  const filtered = query ? all.filter((e) => faIncludes(e.name, query)) : all;
  const selected = all.find((e) => e.catalogId === value) || null;
  useEffect(() => { setHi(0); }, [query, open]);

  const choose = (e) => { onChange(e.catalogId); setQuery(''); setOpen(false); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); return; }
    if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); setQuery(''); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[hi]) { choose(filtered[hi]); onCommit?.(); return; }
      if (selected) onCommit?.();
    }
  };

  if (error) {
    return <ErrorState title={t('reception.booking.servicesFailed')} description={error} retryLabel={t('common.actions.retry')} onRetry={onRetry} />;
  }
  const renderGroup = (title, items) => (items.length ? (
    <div key={title} role="group" aria-label={title}>
      <div style={{ fontSize: '0.65625rem', fontWeight: 700, color: C.textMuted, padding: '6px 10px 2px' }}>{title}</div>
      {items.map((e) => {
        const i = filtered.indexOf(e);
        return (
          <button
            key={e.catalogId}
            type="button"
            role="option"
            aria-selected={e.catalogId === value}
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={() => { choose(e); onCommit?.(); }}
            data-testid={`reception-booking-service-option-${e.catalogId}`}
            style={{
              display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '8px 10px', border: 'none', textAlign: 'start',
              background: i === hi ? C.bg : 'transparent', fontFamily: 'inherit', fontSize: '0.8125rem', cursor: 'pointer', color: C.textPrimary,
              fontWeight: e.catalogId === value ? 700 : 500,
            }}
          >
            {e.name}
            {e.schedulingMode === 'QUEUE' ? (
              <span style={{ fontSize: '0.65625rem', fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: C.amberLight, color: CT.warnInk }}>{t('reception.booking.queueChip')}</span>
            ) : e.rows[0]?.durationMinutes ? (
              <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{formatDigits(e.rows[0].durationMinutes)}′</span>
            ) : null}
          </button>
        );
      })}
    </div>
  ) : null);

  return (
    <div data-testid="reception-booking-service" data-value={value || ''} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={ref}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label={t('reception.booking.service')}
          aria-invalid={!!fieldError}
          value={open ? query : (selected?.name || query)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          // PM-0486: focus does NOT open the list. The dialog opens with focus here,
          // and an open list hid the preselected «ویزیت» and covered «انجام‌دهنده»
          // and «زمان» — the 90 % path must read as "confirm", not "choose". The
          // label is selected so typing replaces it; click, typing or ArrowDown open.
          onFocus={(e) => e.target.select()}
          onClick={() => setOpen(true)}
          onBlur={() => setTimeout(() => { setOpen(false); setQuery(''); }, 120)}
          onKeyDown={onKeyDown}
          placeholder={loading ? t('common.state.loading') : t('reception.booking.serviceSearch')}
          data-testid="reception-booking-service-input"
          style={{
            width: '100%', boxSizing: 'border-box', height: 40, padding: '0 10px', paddingInlineEnd: 30,
            borderRadius: 8, border: `1px solid ${fieldError ? C.red : C.border}`, fontFamily: 'inherit', fontSize: '0.8125rem', fontWeight: 600,
          }}
        />
        <ChevronDown size={14} aria-hidden style={{ position: 'absolute', insetInlineEnd: 10, top: 13, color: C.textMuted }} />
      </div>
      {open ? (
        <div
          role="listbox"
          style={{
            position: 'absolute', insetInline: 0, top: 44, zIndex: 5, background: '#fff', borderRadius: 10,
            border: `1px solid ${C.border}`, boxShadow: '0 12px 30px rgba(15,28,46,.14)', maxHeight: 280, overflowY: 'auto',
          }}
        >
          {loading && !all.length ? (
            <div style={{ padding: 10 }}><Skeleton height={32} /></div>
          ) : !filtered.length ? (
            <div style={{ padding: 10, fontSize: '0.75rem', color: C.textMuted }}>{t('reception.booking.noServices')}</div>
          ) : (
            <>
              {renderGroup(t('reception.booking.groupVisit'), filtered.filter((e) => e.group === 'visit'))}
              {renderGroup(t('reception.booking.groupServices'), filtered.filter((e) => e.group === 'service'))}
            </>
          )}
        </div>
      ) : null}
      {fieldError ? <div role="alert" data-testid="reception-booking-service-error" style={{ fontSize: '0.75rem', color: C.red, marginTop: 4 }}>{fieldError}</div> : null}
      {/* Kept for the eye: a selected service's reason to be unbookable right now. */}
      {!open && selected && !selected.performers.length ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.71875rem', color: CT.warnInk, marginTop: 4 }}>
          <AlertTriangle size={12} aria-hidden />{t('reception.booking.noPerformer')}
        </div>
      ) : null}
    </div>
  );
}

const ServiceCombobox = React.forwardRef(ServiceComboboxInner);
